import { useCallback, useEffect, useRef, useState } from 'react'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { templateThumbUrl } from '../../utils/uiGroups'
import { fieldLabel } from '../../utils/uiLayoutV2'
import classes from './V2Form.module.css'

/**
 * Figma option row ("Types (5)", "Shapes (8)" …): a 14/600 heading with the live option
 * count, 26px ‹ › pager boxes, and 190×140 cards (126×105 illustration, 14px caption, pink
 * 2px outline when chosen) that scroll sideways. `compact` draws the 110×96 position cards.
 *
 * Only the track scrolls when the value changes from elsewhere (presets, AI): the page
 * itself must not jump, which `scrollIntoView` would do.
 */
export function ThumbCarousel({
  field,
  value,
  options,
  onChange,
  templateManifest,
  configuration = null,
  error,
  disabled = false,
  compact = false,
  heading = null,
  withCount = true,
  withPager = true,
}) {
  const { t } = useTranslation()
  const ui = field.ui || {}
  const items = options || field.options || []
  const releaseKey = templateManifest?.releaseKey
  const track = useRef(null)
  const [overflow, setOverflow] = useState({ left: false, right: false })
  const title = heading ?? (ui.heading || fieldLabel(field))

  const measure = useCallback(() => {
    const node = track.current
    if (!node) return
    const max = node.scrollWidth - node.clientWidth
    setOverflow({ left: node.scrollLeft > 4, right: max > 4 && node.scrollLeft < max - 4 })
  }, [])

  useEffect(() => {
    measure()
    const node = track.current
    if (!node) return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [measure, items.length])

  useEffect(() => {
    const node = track.current
    const active = node?.querySelector('[data-selected="true"]')
    if (!node || !active) return
    const start = active.offsetLeft - node.offsetLeft
    const end = start + active.offsetWidth
    if (start < node.scrollLeft) node.scrollTo({ left: Math.max(0, start - 2), behavior: 'smooth' })
    else if (end > node.scrollLeft + node.clientWidth) {
      node.scrollTo({ left: end - node.clientWidth + 2, behavior: 'smooth' })
    }
  }, [value])

  const page = (direction) => {
    const node = track.current
    if (node) node.scrollBy({ left: direction * node.clientWidth * 0.9, behavior: 'smooth' })
  }

  return (
    <div className={classes.thumbs}>
      {(title || withPager) && (
        <div className={classes.thumbsHead}>
          <span className={compact ? classes.label : classes.heading}>
            {title}{withCount && !compact ? ` (${items.length})` : ''}
          </span>
          {withPager && !compact && (overflow.left || overflow.right) && (
            <span className={classes.pager}>
              <button
                type="button"
                className={classes.pagerButton}
                aria-label={t('editorV2.form.common.previousOptions', { title })}
                disabled={!overflow.left}
                onClick={() => page(-1)}
              >
                <IconChevronLeft size={14} stroke={1.8} />
              </button>
              <button
                type="button"
                className={classes.pagerButton}
                aria-label={t('editorV2.form.common.moreOptions', { title })}
                disabled={!overflow.right}
                onClick={() => page(1)}
              >
                <IconChevronRight size={14} stroke={1.8} />
              </button>
            </span>
          )}
        </div>
      )}
      <div ref={track} className={classes.track} onScroll={measure} role="radiogroup" aria-label={title}>
        {items.map((option) => {
          const selected = option.value === value
          const src = templateThumbUrl(releaseKey, ui.thumbBase, option.value, configuration)
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              data-selected={selected ? 'true' : 'false'}
              data-compact={compact ? 'true' : undefined}
              className={classes.thumbCard}
              disabled={disabled}
              onClick={() => { if (!selected) onChange(option.value) }}
            >
              <span className={classes.thumbFrame}>
                {src ? (
                  <img className={classes.thumbImage} src={src} alt="" loading="lazy" draggable="false" />
                ) : (
                  <span className={classes.thumbFallback} aria-hidden="true" />
                )}
              </span>
              <span className={classes.thumbCaption}>{option.label || option.value}</span>
            </button>
          )
        })}
      </div>
      {error && <p className={classes.error}>{error}</p>}
    </div>
  )
}
